import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  interpolateRoute,
  type Integration,
  type IntegrationSyncLog,
  type IntegrationSyncType,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'
import { showMojoClient, ShowMojoClient } from './showmojo.client'
import { tenantTurnerClient, TenantTurnerClient } from './tenant-turner.client'
import { buildiumClient, BuildiumClient } from './buildium.client'
import { hubspotClient, HubspotClient } from './hubspot.client'
import { buildingLinkClient, BuildingLinkClient } from './building-link.client'
import { propertyMeldClient, PropertyMeldClient } from './property-meld.client'
import { rentManagerClient, RentManagerClient } from './rent-manager.client'
import { appFolioClient, AppFolioClient } from './appfolio.client'
import { yardiVoyagerClient, YardiVoyagerClient } from './yardi-voyager.client'
import { yardiBreezeClient, YardiBreezeClient } from './yardi-breeze.client'
import { epicPmClient, EpicPmClient } from './epic-pm.client'
import { entrataClient, EntrataClient } from './entrata.client'
import { rentvineClient, RentvineClient } from './rentvine.client'
import { doorLoopClient, DoorLoopClient } from './doorloop.client'

export interface IntegrationStatus {
  id?: string
  type: string
  name: string
  connected: boolean
  connectedAt?: string
  clientId?: string
  accountId?: string
  metadata?: Record<string, unknown>
  syncLogs?: IntegrationSyncLog[]
  /**
   * Whether the recurring LISTINGS sync job is currently scheduled. Only
   * meaningful for connected integrations that support listings sync; lets the
   * UI show Stop vs Start listing sync.
   */
  listingSyncActive?: boolean
  /** Sync types currently disabled for pull sync. Only present for sync-capable integration types. */
  disabledPullSyncTypes?: IntegrationSyncType[]
}

export interface IntegrationsStatusData {
  integrations: IntegrationStatus[]
}

/**
 * Main Integrations API Client
 * Aggregates all integration-specific clients and provides common operations
 */
export class IntegrationsApiClient extends BaseApiClient {
  // Sub-clients for specific integrations
  readonly showMojo: ShowMojoClient
  readonly tenantTurner: TenantTurnerClient
  readonly buildium: BuildiumClient
  readonly hubspot: HubspotClient
  readonly buildingLink: BuildingLinkClient
  readonly propertyMeld: PropertyMeldClient
  readonly rentManager: RentManagerClient
  readonly appFolio: AppFolioClient
  readonly yardiVoyager: YardiVoyagerClient
  readonly yardiBreeze: YardiBreezeClient
  readonly epicPm: EpicPmClient
  readonly entrata: EntrataClient
  readonly rentvine: RentvineClient
  readonly doorLoop: DoorLoopClient

  constructor() {
    super()
    this.showMojo = showMojoClient
    this.tenantTurner = tenantTurnerClient
    this.buildium = buildiumClient
    this.hubspot = hubspotClient
    this.buildingLink = buildingLinkClient
    this.propertyMeld = propertyMeldClient
    this.rentManager = rentManagerClient
    this.appFolio = appFolioClient
    this.yardiVoyager = yardiVoyagerClient
    this.yardiBreeze = yardiBreezeClient
    this.epicPm = epicPmClient
    this.entrata = entrataClient
    this.rentvine = rentvineClient
    this.doorLoop = doorLoopClient
  }

  async connectApiKeyCredential(
    integrationType: string,
    body: { clientId?: string; clientSecret: string; accountId?: string },
  ): Promise<void> {
    const path = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
      { integrationType },
    )
    await this.request<ApiResponse<{ message: string }>>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async connectAccountCredential(
    integrationType: string,
    body: { accountIdentifier: string; password: string },
  ): Promise<void> {
    const path = interpolateRoute(
      API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
      { integrationType },
    )
    await this.request<ApiResponse<{ message: string }>>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Get status of all integrations for the current user's company
   */
  async getAllIntegrationsStatus(): Promise<IntegrationStatus[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<IntegrationsStatusData>
        >(API_ROUTES.INTEGRATIONS.STATUS)
        return response.data?.integrations ?? []
      },
      {
        errorContext: 'Error fetching integrations status',
      },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Get status of all integrations for a specific company
   */
  async getAllAdminIntegrationsStatus(
    companyId: string,
  ): Promise<IntegrationStatus[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<IntegrationsStatusData>
        >(`${API_ROUTES.ADMIN.INTEGRATIONS.STATUS}?companyId=${companyId}`)
        return response.data?.integrations ?? []
      },
      {
        errorContext: 'Error fetching admin integrations status',
      },
    )
    return result ?? []
  }

  /**
   * Get push-enabled integrations for the current user's company
   */
  async getPushEnabledIntegrations(): Promise<Integration[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Integration[]>>(
          `${API_ROUTES.INTEGRATIONS.GET}?category=PUSH_EVENTS`,
        )
        return response.data ?? []
      },
      {
        errorContext: 'Error fetching push-enabled integrations',
      },
    )
    return result ?? []
  }

  /**
   * Get integrations active for the current user's company that support a
   * per-staff push assignee field (SUP-2305/2347) — Yardi Voyager today.
   */
  async getStaffAssigneeEnabledIntegrations(): Promise<Integration[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Integration[]>>(
          `${API_ROUTES.INTEGRATIONS.GET}?category=STAFF_ASSIGNEE`,
        )
        return response.data ?? []
      },
      {
        errorContext: 'Error fetching staff-assignee-enabled integrations',
      },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Same as above, for a specific company
   */
  async getStaffAssigneeEnabledIntegrationsAdmin(
    companyId: string,
  ): Promise<Integration[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Integration[]>>(
          `${API_ROUTES.ADMIN.INTEGRATIONS.GET}?companyId=${companyId}&category=STAFF_ASSIGNEE`,
        )
        return response.data ?? []
      },
      {
        errorContext:
          'Error fetching admin staff-assignee-enabled integrations',
      },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Get push-enabled integrations for a specific company
   */
  async getPushEnabledIntegrationsAdmin(
    companyId: string,
  ): Promise<Integration[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Integration[]>>(
          `${API_ROUTES.ADMIN.INTEGRATIONS.GET}?companyId=${companyId}&category=PUSH_EVENTS`,
        )
        return response.data ?? []
      },
      {
        errorContext: 'Error fetching admin push-enabled integrations',
      },
    )
    return result ?? []
  }

  /**
   * Trigger manual sync for an integration. Returns per-sync-type status —
   * a type disabled via sync config comes back as 'skipped_disabled' rather
   * than 'queued'.
   */
  async triggerSync(
    integrationId: string,
  ): Promise<Array<{ syncType: string; status: string }>> {
    const result = await withErrorHandling(
      async () => {
        const url = interpolateRoute(API_ROUTES.INTEGRATIONS.SYNC, {
          integrationId,
        })
        const response = await this.request<
          ApiResponse<{
            success: boolean
            jobs: Array<{ syncType: string; status: string }>
          }>
        >(url, {
          method: 'POST',
        })
        return response.data?.jobs ?? []
      },
      {
        errorContext: 'Error triggering integration sync',
      },
    )
    return result!
  }

  /**
   * [ADMIN] Trigger manual sync for an integration
   */
  async triggerSyncAdmin(
    integrationId: string,
  ): Promise<Array<{ syncType: string; status: string }>> {
    const result = await withErrorHandling(
      async () => {
        const url = interpolateRoute(API_ROUTES.ADMIN.INTEGRATIONS.SYNC, {
          integrationId,
        })
        const response = await this.request<
          ApiResponse<{
            success: boolean
            jobs: Array<{ syncType: string; status: string }>
          }>
        >(url, {
          method: 'POST',
        })
        return response.data?.jobs ?? []
      },
      {
        errorContext: 'Error triggering admin integration sync',
      },
    )
    return result!
  }

  /**
   * Sync listings for a specific integration type
   * Generic method that works for all integration types
   */
  async syncListingsForIntegration(integrationType: string): Promise<{
    success: boolean
    recordsProcessed: number
    recordsCreated: number
    recordsUpdated: number
    recordsFailed: number
  }> {
    const result = await withErrorHandling(
      async () => {
        const url = interpolateRoute(API_ROUTES.INTEGRATIONS.SYNC_LISTINGS, {
          integrationType,
        })
        const response = await this.request<
          ApiResponse<{
            success: boolean
            recordsProcessed: number
            recordsCreated: number
            recordsUpdated: number
            recordsFailed: number
          }>
        >(url, {
          method: 'POST',
        })
        return (
          response.data || {
            success: false,
            recordsProcessed: 0,
            recordsCreated: 0,
            recordsUpdated: 0,
            recordsFailed: 0,
          }
        )
      },
      {
        errorContext: 'Error syncing listings from integration',
      },
    )
    return (
      result || {
        success: false,
        recordsProcessed: 0,
        recordsCreated: 0,
        recordsUpdated: 0,
        recordsFailed: 0,
      }
    )
  }

  /**
   * [ADMIN] Sync listings for a specific integration type for a specific company
   * @param companyId - The ID of the company
   * @param integrationType - The integration type (SHOWMOJO, BUILDIUM, etc)
   */
  async syncAdminListingsForIntegration(
    companyId: string,
    integrationType: string,
  ): Promise<{
    success: boolean
    recordsProcessed: number
    recordsCreated: number
    recordsUpdated: number
    recordsFailed: number
  }> {
    const result = await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.SYNC_LISTINGS,
          { integrationType },
        )
        const response = await this.request<
          ApiResponse<{
            success: boolean
            recordsProcessed: number
            recordsCreated: number
            recordsUpdated: number
            recordsFailed: number
          }>
        >(`${url}?companyId=${companyId}`, {
          method: 'POST',
        })
        return (
          response.data || {
            success: false,
            recordsProcessed: 0,
            recordsCreated: 0,
            recordsUpdated: 0,
            recordsFailed: 0,
          }
        )
      },
      {
        errorContext: 'Error syncing listings from integration for company',
      },
    )
    return (
      result || {
        success: false,
        recordsProcessed: 0,
        recordsCreated: 0,
        recordsUpdated: 0,
        recordsFailed: 0,
      }
    )
  }

  /**
   * Stop the recurring LISTINGS sync job for a connected integration (without
   * disconnecting it). Used when switching listings to another source.
   */
  async stopListingSync(integrationType: string): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.STOP_LISTING_SYNC,
          { integrationType },
        )
        await this.request<ApiResponse<unknown>>(url, { method: 'POST' })
      },
      { errorContext: 'Error stopping integration listing sync' },
    )
  }

  /**
   * (Re)start the recurring LISTINGS sync job for a connected integration.
   */
  async startListingSync(integrationType: string): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.START_LISTING_SYNC,
          { integrationType },
        )
        await this.request<ApiResponse<unknown>>(url, { method: 'POST' })
      },
      { errorContext: 'Error starting integration listing sync' },
    )
  }

  /**
   * [ADMIN] Stop the LISTINGS sync job for a company's integration.
   */
  async stopAdminListingSync(
    companyId: string,
    integrationType: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.STOP_LISTING_SYNC,
          { integrationType },
        )
        await this.request<ApiResponse<unknown>>(
          `${url}?companyId=${companyId}`,
          { method: 'POST' },
        )
      },
      { errorContext: 'Error stopping integration listing sync for company' },
    )
  }

  /**
   * [ADMIN] (Re)start the LISTINGS sync job for a company's integration.
   */
  async startAdminListingSync(
    companyId: string,
    integrationType: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.START_LISTING_SYNC,
          { integrationType },
        )
        await this.request<ApiResponse<unknown>>(
          `${url}?companyId=${companyId}`,
          { method: 'POST' },
        )
      },
      { errorContext: 'Error starting integration listing sync for company' },
    )
  }

  /**
   * [ADMIN] Set which sync types are disabled for pull sync for a company's
   * integration. Pass an empty array to re-enable everything.
   */
  async updateAdminSyncConfig(
    companyId: string,
    integrationType: string,
    disabledPullSyncTypes: IntegrationSyncType[],
  ): Promise<{ disabledPullSyncTypes: IntegrationSyncType[] }> {
    const result = await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.SYNC_CONFIG,
          {
            integrationType,
          },
        )
        const response = await this.request<
          ApiResponse<{ disabledPullSyncTypes: IntegrationSyncType[] }>
        >(`${url}?companyId=${companyId}`, {
          method: 'PUT',
          body: JSON.stringify({ disabledPullSyncTypes }),
        })
        return response.data
      },
      { errorContext: 'Error updating integration sync config' },
    )
    return result!
  }

  /**
   * Set which sync types are disabled for pull sync for your integration.
   * Pass an empty array to re-enable everything.
   */
  async updateSyncConfig(
    integrationType: string,
    disabledPullSyncTypes: IntegrationSyncType[],
  ): Promise<{ disabledPullSyncTypes: IntegrationSyncType[] }> {
    const result = await withErrorHandling(
      async () => {
        const url = interpolateRoute(API_ROUTES.INTEGRATIONS.SYNC_CONFIG, {
          integrationType,
        })
        const response = await this.request<
          ApiResponse<{ disabledPullSyncTypes: IntegrationSyncType[] }>
        >(url, {
          method: 'PUT',
          body: JSON.stringify({ disabledPullSyncTypes }),
        })
        return response.data
      },
      { errorContext: 'Error updating integration sync config' },
    )
    return result!
  }
}

// Export singleton instance
export const integrationsClient = new IntegrationsApiClient()

// Export types
export type { SyncListingsResponse } from './showmojo.client'
